import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { getPageByUrl } from '@/api/vpApi'
import { Application } from '@/types/app'
import { PaginatedResponse } from '@/types/pagination'
import { objectToQueryString } from '@/utils/url'
import { ApplicationListParams, applicationApi } from '../api/applicationApi'

export const appQueryKeys = {
  all: ['apps'] as const,
  list: (params?: ApplicationListParams) =>
    [...appQueryKeys.all, 'list', params] as const,
  infiniteList: () => [...appQueryKeys.all, 'infinite-list'] as const,
  details: (id: string) => [...appQueryKeys.all, 'details', id] as const
}

export const useGetApplicationList = (
  params: ApplicationListParams,
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) => {
  return useQuery({
    queryKey: appQueryKeys.list(params),
    queryFn: () => applicationApi.getApplicationList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled
  })
}

export const useGetApplicationsInfinite = () => {
  return useInfiniteQuery({
    queryKey: appQueryKeys.infiniteList(),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<Application[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam: `/v1/applications/?${objectToQueryString({
      limit: 20,
      ordering: 'name'
    })}`
  })
}

export const useGetApplicationDetails = (id: string) => {
  return useQuery({
    queryKey: appQueryKeys.details(id),
    queryFn: () => applicationApi.getApplicationDetails(id)
  })
}

export const useCreateApplication = () => {
  return useMutation({
    mutationFn: applicationApi.createApplication
  })
}

export const useUpdateApplication = (appId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: applicationApi.updateApplication,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: appQueryKeys.details(appId)
      })
    }
  })
}

export const useDeleteApplication = () => {
  return useMutation({
    mutationFn: applicationApi.deleteApplication
  })
}
